import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import moment from 'moment';
import DateRangePicker from 'react-daterange-picker';
import 'react-daterange-picker/dist/css/react-calendar.css';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import styles from './DateRangeSelectionModal.module.scss';
import {
  CUSTOM_RANGE_OPTION_VALUE,
  DATE_SELECTION_MODES,
  PERIOD_SELECTION_TYPES,
  formatPeriodDisplayLabel,
  getDefaultHistoricalRange,
  getDefaultTimes,
  getPeriodSelectOptions,
  getFullYearDateRange,
  getTodayRange,
  resolvePeriodSelectionType,
  toPickerDateRange,
  toStoredDates,
  validateDateRange,
  validateTimeRange,
} from '../../dateUtils';

const buildDraftFromProps = props => {
  const storedDates = toStoredDates(
    props.dates,
    props.startDate,
    props.endDate,
  );
  const defaultTimes = getDefaultTimes();
  return {
    dateSelectionMode: props.dateSelectionMode,
    selectedYear: props.selectedYear,
    periodSelectionType: resolvePeriodSelectionType(props),
    startDate: storedDates.start,
    endDate: storedDates.end,
    dates: storedDates,
    startTime: props.startTime || defaultTimes.startTime,
    endTime: props.endTime || defaultTimes.endTime,
    localError: null,
  };
};

const CALENDAR_GAP = 8;

const DateRangeSelectionModal = ({ isOpen, onClose, onApply, ...props }) => {
  const [draft, setDraft] = useState(() => buildDraftFromProps(props));
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [focusedInput, setFocusedInput] = useState(null);
  const [startTimePanel, setStartTimePanel] = useState(null);
  const [endTimePanel, setEndTimePanel] = useState(null);
  const periodSelectAnchorRef = useRef(null);
  const calendarPanelRef = useRef(null);

  const updateCalendarPosition = useCallback(() => {
    if (!periodSelectAnchorRef.current) {
      return;
    }
    const rect = periodSelectAnchorRef.current.getBoundingClientRect();
    setCalendarPosition({
      top: rect.bottom + CALENDAR_GAP,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      setDraft(buildDraftFromProps(props));
      setFocusedInput(null);
      setShowCalendar(false);
    }
  }, [
    isOpen,
    props.dateSelectionMode,
    props.startDate,
    props.endDate,
    props.startTime,
    props.endTime,
    props.periodSelectionType,
    props.selectedYear,
  ]);

  useEffect(() => {
    if (!showCalendar) {
      return undefined;
    }
    updateCalendarPosition();
    window.addEventListener('resize', updateCalendarPosition);
    window.addEventListener('scroll', updateCalendarPosition, true);
    return () => {
      window.removeEventListener('resize', updateCalendarPosition);
      window.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [showCalendar, updateCalendarPosition]);

  useEffect(() => {
    if (!showCalendar) {
      return undefined;
    }

    const handlePointerDown = event => {
      const target = event.target;
      if (
        calendarPanelRef.current?.contains(target) ||
        periodSelectAnchorRef.current?.contains(target)
      ) {
        return;
      }
      setShowCalendar(false);
      setFocusedInput(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showCalendar]);

  if (!isOpen) {
    return null;
  }

  const periodSelectOptions = getPeriodSelectOptions();
  const isTodayMode = draft.dateSelectionMode === DATE_SELECTION_MODES.TODAY;
  const isCustomRange =
    draft.periodSelectionType === PERIOD_SELECTION_TYPES.CUSTOM;
  const selectedPeriodOption = isCustomRange
    ? periodSelectOptions.find(
        option => option.value === CUSTOM_RANGE_OPTION_VALUE,
      )
    : periodSelectOptions.find(option => option.value === draft.selectedYear) ||
      periodSelectOptions.find(option => typeof option.value === 'number');
  const historicalMaxDate = moment().subtract(1, 'day').toDate();
  const pickerValue = toPickerDateRange(
    draft.dates,
    draft.startDate,
    draft.endDate,
  );

  const getPreviewLabel = () =>
    formatPeriodDisplayLabel({
      dateSelectionMode: draft.dateSelectionMode,
      dates: draft.dates,
      startDate: draft.startDate,
      endDate: draft.endDate,
      startTime: draft.startTime,
      endTime: draft.endTime,
    }) || 'No range selected';

  const setDraftField = updates =>
    setDraft(prev => ({ ...prev, ...updates, localError: null }));

  const handleModeChange = mode => {
    if (mode === DATE_SELECTION_MODES.TODAY) {
      setDraftField({
        dateSelectionMode: mode,
        ...getTodayRange(),
      });
    } else {
      const year = moment().year();
      const yearRange = getFullYearDateRange(year);
      setDraftField({
        dateSelectionMode: mode,
        periodSelectionType: PERIOD_SELECTION_TYPES.YEAR,
        selectedYear: year,
        startDate: yearRange.startDate,
        endDate: yearRange.endDate,
        dates: yearRange.dates,
      });
    }
  };

  const handleCalendarSelect = selectedDates => {
    const storedDates = toStoredDates(selectedDates);
    setDraftField({
      periodSelectionType: PERIOD_SELECTION_TYPES.CUSTOM,
      startDate: storedDates.start,
      endDate: storedDates.end,
      dates: storedDates,
    });
    setShowCalendar(false);
    setFocusedInput(null);
  };

  const handlePeriodSelectChange = option => {
    if (option?.value === CUSTOM_RANGE_OPTION_VALUE) {
      setDraftField({
        periodSelectionType: PERIOD_SELECTION_TYPES.CUSTOM,
        ...getDefaultHistoricalRange(),
      });
      setShowCalendar(true);
      requestAnimationFrame(updateCalendarPosition);
      return;
    }

    const year = option?.value || moment().year();
    const yearRange = getFullYearDateRange(year);
    if (yearRange.error) {
      setDraft(prev => ({ ...prev, localError: yearRange.error }));
      return;
    }
    setDraftField({
      dateSelectionMode: DATE_SELECTION_MODES.HISTORICAL,
      periodSelectionType: PERIOD_SELECTION_TYPES.YEAR,
      selectedYear: year,
      startDate: yearRange.startDate,
      endDate: yearRange.endDate,
      dates: yearRange.dates,
    });
    setShowCalendar(false);
  };

  const handleApply = () => {
    const storedDates = toStoredDates(draft.dates, draft.startDate, draft.endDate);
    const dateValidationError = validateDateRange(
      storedDates.start,
      storedDates.end,
      draft.dateSelectionMode,
      draft.periodSelectionType,
    );
    if (dateValidationError) {
      setDraft(prev => ({ ...prev, localError: dateValidationError }));
      return;
    }
    const timeValidationError = validateTimeRange(
      storedDates.start,
      storedDates.end,
      draft.startTime,
      draft.endTime,
    );
    if (timeValidationError) {
      setDraft(prev => ({ ...prev, localError: timeValidationError }));
      return;
    }
    onApply({
      dateSelectionMode: draft.dateSelectionMode,
      selectedYear: draft.selectedYear,
      periodSelectionType: draft.periodSelectionType,
      startDate: storedDates.start,
      endDate: storedDates.end,
      dates: storedDates,
      startTime: draft.startTime,
      endTime: draft.endTime,
    });
    onClose();
  };

  const handleOverlayClick = event => {
    if (showCalendar) {
      return;
    }
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleCloseCalendar = () => {
    setShowCalendar(false);
    setFocusedInput(null);
  };

  const calendarPortal =
    showCalendar &&
    isCustomRange &&
    ReactDOM.createPortal(
      <>
        <div
          className={styles.calendarBackdrop}
          onClick={handleCloseCalendar}
          role="presentation"
        />
        <div
          ref={calendarPanelRef}
          className={styles.calendarFloatPortal}
          style={{
            top: calendarPosition.top,
            left: calendarPosition.left,
          }}
          onClick={event => event.stopPropagation()}
          role="dialog"
          aria-label="Select custom date range">
          <DateRangePicker
            numberOfCalendars={1}
            selectionType="range"
            onSelect={handleCalendarSelect}
            value={pickerValue}
            singleDateRange={true}
            maximumDate={historicalMaxDate}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
          />
        </div>
      </>,
      document.body,
    );

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="date-range-modal-title">
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <h2 id="date-range-modal-title" className={styles.title}>
              Select reporting period
            </h2>
            <p className={styles.subtitle}>
              Choose Today for current data, a historical year, or a custom
              range. Today cannot be combined with past dates.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.modeWrapper}>
            <button
              type="button"
              className={[
                styles.modeBtn,
                isTodayMode ? styles.modeBtnActive : '',
              ].join(' ')}
              onClick={() => handleModeChange(DATE_SELECTION_MODES.TODAY)}>
              Today
            </button>
            <button
              type="button"
              className={[
                styles.modeBtn,
                !isTodayMode ? styles.modeBtnActive : '',
              ].join(' ')}
              onClick={() =>
                handleModeChange(DATE_SELECTION_MODES.HISTORICAL)
              }>
              Historical
            </button>
          </div>

          {isTodayMode ? (
            <div className={styles.todayPanel}>
              <div className={styles.todayLabel}>
                {moment().format('dddd, D MMMM YYYY')}
              </div>
              <p className={styles.todayHint}>
                Report and download will include today&apos;s data only.
              </p>
            </div>
          ) : (
            <div className={styles.periodSection}>
              <p className={styles.sectionLabel}>Select period</p>
              <div className={styles.periodSelectRow}>
                <div
                  ref={periodSelectAnchorRef}
                  className={styles.periodSelect}>
                  <Select
                    options={periodSelectOptions}
                    value={selectedPeriodOption}
                    onChange={handlePeriodSelectChange}
                    classNamePrefix="period-select"
                    isSearchable={false}
                  />
                </div>
              </div>

              {isCustomRange && !showCalendar ? (
                <button
                  type="button"
                  className={styles.changeDatesBtn}
                  onClick={() => {
                    setShowCalendar(true);
                    requestAnimationFrame(updateCalendarPosition);
                  }}>
                  Change dates
                </button>
              ) : null}
            </div>
          )}

          <div className={styles.timeSection}>
            <p className={styles.sectionLabel}>Time range</p>
            <div className={styles.timeRow}>
              <div className={styles.timeField}>
                <label className={styles.timeLabel} htmlFor="start-time">
                  Start time
                </label>
                <TimePicker
                  id="start-time"
                  addon={panel => setStartTimePanel(panel)}
                  showSecond={false}
                  value={draft.startTime}
                  onChange={time => {
                    setDraftField({ startTime: time });
                    if (
                      startTimePanel?.state?.currentSelectPanel === 'minute'
                    ) {
                      startTimePanel.close();
                    }
                  }}
                  className={styles.timepicker}
                  placeholder="Start time"
                  format="HH:mm"
                />
              </div>
              <div className={styles.timeField}>
                <label className={styles.timeLabel} htmlFor="end-time">
                  End time
                </label>
                <TimePicker
                  id="end-time"
                  addon={panel => setEndTimePanel(panel)}
                  showSecond={false}
                  value={draft.endTime}
                  onChange={time => {
                    setDraftField({ endTime: time });
                    if (endTimePanel?.state?.currentSelectPanel === 'minute') {
                      endTimePanel.close();
                    }
                  }}
                  className={styles.timepicker}
                  placeholder="End time"
                  format="HH:mm"
                />
              </div>
            </div>
          </div>

          <div className={styles.preview}>
            <span className={styles.previewLabel}>Selected</span>
            <span className={styles.previewValue}>{getPreviewLabel()}</span>
          </div>

          {draft.localError ? (
            <div className={styles.error} role="alert">
              {draft.localError}
            </div>
          ) : null}
        </div>

        <div className={styles.footer}>
          {/* <button
            type="button"
            className={[styles.footerBtn, styles.cancelBtn].join(' ')}
            onClick={onClose}>
            Cancel
          </button> */}
          <button
            type="button"
            className={[styles.footerBtn, styles.applyBtn].join(' ')}
            onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
      {calendarPortal}
    </div>
  );
};

export default DateRangeSelectionModal;
